import { useQuery } from '@tanstack/react-query'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { Status } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { validateConflicts } from '@/features/bidding/endpoints/bids'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { ApiCapacityOffer } from '@/features/bidding/types/api'
import type { Ptu } from '@/features/bidding/types/bid'
import { buildDefaultOfferedBid } from '@/features/bidding/utils/calculations/calculateBasisForNewBid'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'

export type PtuConflict = {
  ptu: Ptu
  conflicts: boolean
  reason?: string
}

/**
 * For each group compile a list of all PTUs and whether they would create conflicts if volume was greater than zero
 */
export function useBidOfferPtuConflicts(eligibleActivationGroups: ActivationGroup[], groupParams: GroupedBidParams) {
  const { selectedCountry } = useBiddingContext()
  const payload = eligibleActivationGroups.map((ag) => mapToOneMegawattBid(ag, groupParams, selectedCountry))
  const { data = null, ...queryResult } = useQuery({
    queryKey: ['VALIDATE_BID', payload],
    queryFn: () => validateConflicts(payload),
  })
  useErrorHandler(queryResult.isError)

  const conflictivePtusByGroup = new Map<string, PtuConflict[]>()
  if (!data) {
    return {
      conflictivePtusByGroup: conflictivePtusByGroup,
      ...queryResult,
    }
  }

  for (const ag of eligibleActivationGroups) {
    const issues = data?.issues.find((issue) => issue.activationGroupUuid === ag.uuid)
    if (!issues) {
      conflictivePtusByGroup.set(ag.uuid, [])
      continue
    }

    const ptus = issues.conflicts.map((conflict) => {
      return {
        ptu: conflict.ptu,
        conflicts: true,
        reason:
          'Would conflict with bid for group: ' +
          conflict.conflictingActivationGroupCode +
          ', market: ' +
          conflict.conflictingMarket,
      }
    })
    conflictivePtusByGroup.set(ag.uuid, ptus)
  }
  return {
    conflictivePtusByGroup: conflictivePtusByGroup,
    ...queryResult,
  }
}

/**
 * to know which PTUs would create conflicts, we can send a bid with all its PTUs set to 1MW to the validation endpoint
 */
const mapToOneMegawattBid = (
  activationGroup: ActivationGroup,
  groupParams: GroupedBidParams,
  selectedCountryCode: CountryIdentifier,
) => {
  // we reuse the default bid builder to get the PTUs for this group
  const extendedBidPtus = buildDefaultOfferedBid(selectedCountryCode, groupParams.deliveryDay, groupParams.bidType)

  const bid: ApiCapacityOffer = {
    context: {
      portfolio: activationGroup.portfolio,
      marketProgram: activationGroup.marketProgram,
      activationGroupUuid: activationGroup.uuid,
      deliveryDay: groupParams.deliveryDay.toISODate(),
      bidType: groupParams.bidType,
    },
    offeredValues: extendedBidPtus.map((ptu) => {
      return {
        ptu: ptu.ptu,
        volume: { quantity: 1000000, unit: 'WATTS' },
      }
    }),
    status: Status.DRAFT,
  }
  return bid
}
